import { IUser } from '../../../../models/other/user.interface';
import { INotification } from '../../../../models/template/notifications-model.interface';
import {
  TemplateVersionDecision,
  TemplateVersionDecisionType,
  TemplateVersionSummary,
} from '../../../../models/template-version/template-version.interface';

export type DecisionTone = 'green' | 'amber' | 'red' | 'grey';

export interface DecisionTypeOption { key: TemplateVersionDecisionType; label: string; tone: DecisionTone; }

export const DECISION_TYPES: DecisionTypeOption[] = [
  { key: 'approved', label: 'Approved', tone: 'green' },
  { key: 'approved_with_changes', label: 'Approved with changes', tone: 'amber' },
  { key: 'changes_required', label: 'Changes required', tone: 'red' },
  { key: 'other', label: 'Other decision', tone: 'grey' },
];

export function decisionType(type: string): DecisionTypeOption {
  const found = DECISION_TYPES.find(d => d.key === type);
  return found || DECISION_TYPES[3];
}

export function decisionCounts(decisions: TemplateVersionDecision[] | null | undefined): { red: number; amber: number; green: number } {
  if (!decisions) {
    return { red: 0, amber: 0, green: 0 };
  }
  let red = 0;
  let amber = 0;
  let green = 0;
  for (const d of decisions) {
    const tone = decisionType(d.type).tone;
    if (tone === 'red') red++;
    else if (tone === 'amber') amber++;
    else if (tone === 'green') green++;
  }
  return { red, amber, green };
}

export function authorName(user: IUser | null | undefined): string {
  return user?.name || user?.username || 'Content Rocket';
}

export function sourceLabel(version: TemplateVersionSummary): string {
  if (version.source === 'manual') {
    return 'Manual';
  }
  if (version.source === 'responsys_publish') {
    return 'Published';
  }
  if (version.source === 'restore') {
    return `Restored from Version ${version.restoredFromVersion}`;
  }
  return '';
}

export function clampPinPercent(value: number): number {
  const clamped = Math.max(2, Math.min(98, value));
  return Math.round(clamped * 10) / 10;
}

export function isCompleted(comment: INotification): boolean {
  return !!comment.completed;
}

export function commentNumbers(comments: INotification[]): Map<string, number> {
  const numbered: { notificationId: string; version: number; createdDate: string }[] = [];
  for (const c of comments) {
    if (c.version != null && typeof c.version === 'number' && c.notificationId) {
      numbered.push({
        notificationId: c.notificationId,
        version: c.version,
        createdDate: c.createdDate,
      });
    }
  }
  numbered.sort((a, b) => {
    if (a.version !== b.version) {
      return a.version - b.version;
    }
    const aTime = new Date(a.createdDate).getTime() || 0;
    const bTime = new Date(b.createdDate).getTime() || 0;
    return aTime - bTime;
  });
  const result = new Map<string, number>();
  numbered.forEach((item, index) => {
    result.set(item.notificationId, index + 1);
  });
  return result;
}

export interface CommentGroup {
  version: TemplateVersionSummary | null;
  comments: INotification[];
  openCount: number;
}

export function groupComments(versions: TemplateVersionSummary[], comments: INotification[]): CommentGroup[] {
  // Create groups for each version in order
  const groups: CommentGroup[] = versions.map(v => ({
    version: v,
    comments: [],
    openCount: 0,
  }));

  // Assign comments to groups
  for (const comment of comments) {
    if (comment.version != null && typeof comment.version === 'number') {
      // Find matching version group
      const groupIndex = groups.findIndex(g => g.version && g.version.version === comment.version);
      if (groupIndex !== -1) {
        groups[groupIndex].comments.push(comment);
      }
      // If no matching version, drop the comment
    }
    // Comments with null/undefined version will go to the trailing group
  }

  // Sort comments within each group by createdDate descending (newest first)
  for (const group of groups) {
    group.comments.sort((a, b) => {
      const aTime = new Date(a.createdDate).getTime();
      const bTime = new Date(b.createdDate).getTime();
      return bTime - aTime;
    });
    // Calculate openCount
    group.openCount = group.comments.filter(c => !isCompleted(c)).length;
  }

  // Handle unversioned comments (version null/undefined)
  const unversionedComments = comments.filter(c => c.version == null || typeof c.version !== 'number');
  if (unversionedComments.length > 0) {
    // Sort unversioned comments by createdDate descending
    unversionedComments.sort((a, b) => {
      const aTime = new Date(a.createdDate).getTime();
      const bTime = new Date(b.createdDate).getTime();
      return bTime - aTime;
    });
    const openCount = unversionedComments.filter(c => !isCompleted(c)).length;
    groups.push({
      version: null,
      comments: unversionedComments,
      openCount,
    });
  }

  return groups;
}

export function commentCountLabel(group: CommentGroup): string {
  const n = group.comments.length;
  const base = `${n} comment${n === 1 ? '' : 's'}`;
  if (group.openCount > 0) {
    return `${base} · ${group.openCount} open`;
  }
  return base;
}

export type TimelineEventType = 'created' | 'version' | 'published' | 'restored' | 'decision' | 'comment' | 'completed';

export interface TimelineEvent {
  type: TimelineEventType;
  title: string;
  author: string;
  date: string;
  tone: DecisionTone | null;
  glyph: '+' | '✓' | '';
}

export function buildTimeline(versions: TemplateVersionSummary[], comments: INotification[]): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Version events
  for (const v of versions) {
    const author = authorName(v.createdBy);
    const date = v.createdDate;
    const tone: DecisionTone | null = null;
    const glyph: '+' | '✓' | '' = '+';

    let type: TimelineEventType;
    let title: string;

    if (v.source === 'responsys_publish') {
      type = 'published';
      title = `Published to Responsys · Version ${v.version}`;
    } else if (v.source === 'restore') {
      type = 'restored';
      title = `Version ${v.restoredFromVersion} restored as Version ${v.version}`;
    } else if (v.version === 1) {
      type = 'created';
      title = 'Proof created · Version 1';
    } else {
      type = 'version';
      title = `Proof updated to Version ${v.version}`;
    }

    events.push({ type, title, author, date, tone, glyph });
  }

  // Decision events
  for (const v of versions) {
    for (const d of v.decisions ?? []) {
      const dt = decisionType(d.type);
      events.push({
        type: 'decision',
        title: `${dt.label} · Version ${v.version}`,
        author: authorName(d.createdBy),
        date: d.createdDate,
        tone: dt.tone,
        glyph: '',
      });
    }
  }

  // Comment events
  for (const c of comments) {
    if (c.version == null || typeof c.version !== 'number') {
      continue;
    }

    // Comment event
    events.push({
      type: 'comment',
      title: `Comment added · Version ${c.version}`,
      author: authorName(c.userInfo),
      date: c.createdDate,
      tone: null,
      glyph: '',
    });

    // Completed event (only if completed and has completedDate)
    if (isCompleted(c) && c.completedDate) {
      events.push({
        type: 'completed',
        title: `Comment marked as done · Version ${c.version}`,
        author: authorName(c.completedBy),
        date: c.completedDate,
        tone: null,
        glyph: '✓',
      });
    }
  }

  // Sort by date descending (stable sort preserves insertion order for equal dates)
  events.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  return events;
}
